import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLandReadiness } from '../../services/api/implementation/impl';
import {
  LAND_REQUIREMENT_STATE_LABELS,
  LAND_REQUIREMENT_STATE_STYLES
} from '../../constants/land';

/**
 * Land readiness checklist.
 *
 * Rendered entirely from GET /land/:id/readiness. The rules live in the backend's
 * landReadinessPolicy; duplicating them here would guarantee drift, and the progress bar
 * would then disagree with the gate that actually blocks a save.
 *
 * NOT A VERIFICATION BADGE. Every check here is arithmetic and string comparison over
 * what was typed in — no government record is consulted, by decision. A green tick means
 * "the numbers are internally consistent", never "this title is good".
 */
const LandReadinessPanel = ({ landId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['landReadiness', landId],
    queryFn: () => getLandReadiness(landId),
    enabled: Boolean(landId),
    retry: false
  });

  // Silent when unavailable. This is supplementary information on a page that works
  // perfectly well without it — an error banner here would be noise, not help.
  if (isLoading || isError || !data) return null;

  const isLegacy = data.status === 'legacy';
  const failedChecks = (data.checks || []).filter((check) => !check.ok);

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-stone-700">Record completeness</span>
        <span className="text-sm text-stone-500">{data.percent}% complete</span>
      </div>

      <div className="mt-2 h-1.5 w-full rounded bg-stone-200">
        <div
          className="h-1.5 rounded bg-emerald-600 transition-all"
          style={{ width: `${data.percent}%` }}
        />
      </div>

      {isLegacy && (
        <p className="mt-2 rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
          This land was added before these requirements existed. The gaps below are shown
          for information — nothing here blocks you from editing or saving it.
        </p>
      )}

      <ul className="mt-3 flex flex-col gap-1.5">
        {(data.requirements || []).map((requirement) => {
          const style =
            LAND_REQUIREMENT_STATE_STYLES[requirement.state] ||
            LAND_REQUIREMENT_STATE_STYLES.not_applicable;

          return (
            <li key={requirement.key} className="flex items-start gap-2 text-xs">
              <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${style.dot}`} />
              <span className="flex-1">
                <span className={style.text}>{requirement.label}</span>
                {requirement.detail && (
                  <span className="block text-stone-500">{requirement.detail}</span>
                )}
              </span>
              <span className="shrink-0 text-stone-400">
                {LAND_REQUIREMENT_STATE_LABELS[requirement.state] || requirement.state}
                {requirement.state === 'missing' && !requirement.blocking && ' (optional)'}
              </span>
            </li>
          );
        })}
      </ul>

      {failedChecks.length > 0 && (
        <div className="mt-3 flex flex-col gap-1 rounded border border-amber-300 bg-amber-50 px-3 py-2">
          {failedChecks.map((check) => (
            <p
              key={check.code}
              className={`text-xs ${check.severity === 'error' ? 'text-red-800' : 'text-amber-800'}`}>
              {check.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default LandReadinessPanel;
